import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Lock, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import '../../components/Auth/Auth.css';

export default function ResetPassword() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const { updatePassword, session } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // If no session from reset link, redirect to login
        if (!session) {
            const timer = setTimeout(() => {
                navigate('/login');
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [session, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        try {
            await updatePassword(password);
            setSuccess(true);
            setTimeout(() => navigate('/dashboard'), 2000);
        } catch (err) {
            setError(err.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="auth">
                <div className="auth__container" style={{ textAlign: 'center' }}>
                    <CheckCircle size={48} color="#059669" style={{ marginBottom: 16 }} />
                    <h1 className="auth__title">Password Updated!</h1>
                    <p className="auth__subtitle">Redirecting to dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="auth">
            <div className="auth__container">
                <div className="auth__header">
                    <a href="/" className="auth__brand">REVTRONE</a>
                    <h1 className="auth__title">Set New Password</h1>
                    <p className="auth__subtitle">Enter your new password below</p>
                </div>

                <form className="auth__form" onSubmit={handleSubmit}>
                    {error && <div className="auth__error">{error}</div>}

                    <div className="auth__field">
                        <label className="auth__label" htmlFor="password">New Password</label>
                        <div className="auth__inputWrapper">
                            <Lock className="auth__inputIcon" aria-hidden="true" />
                            <input
                                id="password"
                                type="password"
                                className="auth__input"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                            />
                        </div>
                    </div>

                    <div className="auth__field">
                        <label className="auth__label" htmlFor="confirmPassword">Confirm Password</label>
                        <div className="auth__inputWrapper">
                            <Lock className="auth__inputIcon" aria-hidden="true" />
                            <input
                                id="confirmPassword"
                                type="password"
                                className="auth__input"
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                minLength={6}
                            />
                        </div>
                    </div>

                    <button className="auth__submit" type="submit" disabled={loading}>
                        {loading ? (
                            <Loader2 className="auth__spinner" />
                        ) : (
                            <>
                                Update Password
                                <ArrowRight className="auth__submitIcon" aria-hidden="true" />
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
